// src/features/equipment/pages/EditEquipmentPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import EquipmentForm from '../components/EquipmentForm';

// Hooks
import { useEquipment } from '../hooks/useEquipment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormSection, FormActions, FormSwitchField } from '../../../components/common/form';

export default function EditEquipmentPage() {
  const { t } = useTranslation(['equipment', 'common']);
  const navigate = useNavigate();
  const { equipmentId } = useParams();
  const { showSuccess, showError } = useSnackbar();
  const { navigateBack } = useNavigationStack();

  // State
  const [equipment, setEquipment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    equipment_type: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    batch_number: '',
    max_voltage_kv: '',
    last_receipt_control_date: '',
    notes: '',
    is_active: true,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    fetchEquipmentById,
    updateEquipment,
    loading: updating,
    error,
    clearError,
  } = useEquipment();

  // Load equipment data
  useEffect(() => {
    const loadEquipmentData = async () => {
      if (!equipmentId) return;

      try {
        setLoading(true);
        const result = await fetchEquipmentById(equipmentId);
        
        if (result.success) {
          const equipmentData = result.data;
          setEquipment(equipmentData);
          setFormData({
            name: equipmentData.name || '',
            equipment_type: equipmentData.equipment_type || '',
            manufacturer: equipmentData.manufacturer || '',
            model: equipmentData.model || '',
            serial_number: equipmentData.serial_number || '',
            batch_number: equipmentData.batch_number || '',
            max_voltage_kv: equipmentData.max_voltage_kv || '',
            last_receipt_control_date: equipmentData.last_receipt_control_date || '',
            notes: equipmentData.notes || '',
            is_active: equipmentData.is_active !== undefined ? equipmentData.is_active : true,
          });
        } else {
          showError(t('equipment:errors.load_failed'));
        }
      } catch (error) {
        showError(t('equipment:errors.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadEquipmentData();
  }, [equipmentId, fetchEquipmentById, showError, t]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleActiveToggle = useCallback((event) => {
    handleInputChange('is_active', event.target.checked);
  }, [handleInputChange]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('equipment:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('equipment:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('equipment:validation.name_max_length');
    }

    if (formData.max_voltage_kv && parseFloat(formData.max_voltage_kv) <= 0) {
      errors.max_voltage_kv = t('equipment:validation.max_voltage_positive');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm() || !equipment) {
      return;
    }

    try {
      const updateData = {
        name: formData.name.trim(),
        manufacturer: formData.manufacturer.trim() || null,
        model: formData.model.trim() || null,
        serial_number: formData.serial_number.trim() || null,
        batch_number: formData.batch_number.trim() || null,
        max_voltage_kv: formData.max_voltage_kv ? parseFloat(formData.max_voltage_kv) : null,
        notes: formData.notes.trim() || null,
        is_active: formData.is_active
      };

      const result = await updateEquipment(equipment.equipment_id, updateData);

      if (result.success) {
        showSuccess(t('equipment:success.equipment_updated'));
        // Navigate back to equipment details page
        const fallbackPath = equipment ? `/equipment/${equipment.equipment_id}` : '/equipment';
        navigateBack(fallbackPath);
      } else {
        showError(result.error || t('equipment:errors.update_failed'));
      }
    } catch (error) {
      showError(t('equipment:errors.unexpected_error'));
    }
  }, [validateForm, equipment, formData, updateEquipment, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    // Navigate back to equipment details page
    const fallbackPath = equipment ? `/equipment/${equipment.equipment_id}` : '/equipment';
    navigateBack(fallbackPath);
  }, [navigate, equipment]);

  const handleBack = useCallback(() => {
    // Use navigation stack to go back
    const fallbackPath = equipment ? `/equipment/${equipment.equipment_id}` : '/equipment';
    navigateBack(fallbackPath);
  }, [navigateBack, equipment]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!equipment) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/equipment')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {t('equipment:messages.equipment_not_found')}
          </Typography>
        </Box>
        <Alert severity="error">
          {t('equipment:messages.equipment_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('equipment:edit_equipment')}
      subtitle={t('equipment:form.edit_title', { name: equipment.name })}
      crumb={equipment.name}
      onBack={handleBack}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <EquipmentForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={updating}
        mode="edit"
        showRoomInfo={false}
      />

      <FormSection title={t('equipment:status_form.title')}>
        <FormSwitchField
          checked={formData.is_active}
          onChange={handleActiveToggle}
          disabled={updating || equipment?.status === 'in_maintenance'}
          label={formData.is_active ? t('equipment:status_form.active_equipment') : t('equipment:status_form.inactive_equipment')}
          description={equipment?.status === 'in_maintenance'
            ? t('equipment:status_form.locked_in_maintenance')
            : formData.is_active ? t('equipment:status_form.active_description') : t('equipment:status_form.inactive_description')}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={updating ? t('equipment:actions.updating') : t('equipment:actions.update_equipment')}
        submitIcon={<SaveIcon />}
        loading={updating}
      />
    </FormPage>
  );
}
